import { Group, InfiniteTable } from '@/components/_common';
import { useWorkOrderMigrateConsult } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import StatusTag from '@/components/_common/StatusTag';
import {
  WorkOrderComplementStatus,
  workorderComplementColor,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { WorkorderColumn } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { Tag } from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useState } from 'react';
import {
  WorkOrderMigrateConsult,
  WorkOrderMigrateConsultListRequest,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useDebounce, useDisclosure } from '@/hooks';
import MigrationConsultDetailModal from './MigrationConsultDetailModal';
import EyeIcon from '@/components/_common/EyeIcon';
import MigrationBindComplements from './MigrationBindComplements';
import MigrationWorkOrderBinded from './MigrationWorkOrderBinded';

interface Props {
  queryParams?: WorkOrderMigrateConsultListRequest;
}

export default function WorkOrderMigrationConsultList(props: Props) {
  const intl = useIntl();

  const queryDebounce = useDebounce(props.queryParams, 500);

  const [workOrder, setWorkOrder] = useState<WorkOrderMigrateConsult>();

  const [opened, action] = useDisclosure(false, {
    onClose: () => setWorkOrder(undefined),
  });

  const workOrderMigrateConsultList = useWorkOrderMigrateConsult(queryDebounce);

  const workOrders = flatInfiniteData(workOrderMigrateConsultList.data);

  return (
    <>
      <InfiniteTable
        dataSource={workOrders}
        loading={workOrderMigrateConsultList.isLoading}
        fetchNextPage={workOrderMigrateConsultList.fetchNextPage}
        isFetching={workOrderMigrateConsultList.isFetching}
        hasNextPage={workOrderMigrateConsultList.hasNextPage}
        columns={[
          {
            key: WorkorderColumn.Number,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Number}`,
            }),
            dataIndex: 'complementIdentifier',
            ellipsis: true,
            width: 100,
          },
          {
            key: WorkorderColumn.ComplementOrigin,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.ComplementOrigin}`,
            }),
            dataIndex: 'origin',
            render: (value) => value || '-',
            ellipsis: true,
            width: 150,
          },
          {
            key: WorkorderColumn.Company,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Company}`,
            }),
            dataIndex: ['companyName'],
            ellipsis: true,
            width: 120,
          },
          {
            key: WorkorderColumn.Estimator,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Estimator}`,
            }),
            dataIndex: ['estimator'],
            ellipsis: true,
            width: 120,
          },
          {
            key: WorkorderColumn.Customer,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Customer}`,
            }),
            dataIndex: ['customer'],
            ellipsis: true,
            width: 120,
          },
          {
            key: WorkorderColumn.Insurer,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Insurer}`,
            }),
            dataIndex: ['insurer'],
            ellipsis: true,
            width: 120,
            render: (value: string) => value || '-',
          },
          {
            key: WorkorderColumn.Prisma,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Prisma}`,
            }),
            dataIndex: 'prisma',
            ellipsis: true,
            width: 120,
          },
          {
            key: WorkorderColumn.fleet,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.fleet}`,
            }),
            dataIndex: 'fleet',
            ellipsis: true,
            width: 75,
            render: (value: string) => value || '-',
          },
          {
            key: WorkorderColumn.Plate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Plate}`,
            }),
            dataIndex: 'licensePlate',
            ellipsis: true,
            width: 75,
            render: (value: string) => value || '-',
          },
          {
            key: WorkorderColumn.Vehicle,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Vehicle}`,
            }),
            dataIndex: 'vehicle',
            ellipsis: true,
            width: 120,
            render: (_, item) =>
              item.model || item.brand
                ? `${item?.brand || ''} ${item?.model || ''}`
                : '-',
          },
          {
            key: WorkorderColumn.Observation,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Observation}`,
            }),
            dataIndex: ['observation'],
            ellipsis: true,
            width: 120,
          },
          {
            key: WorkorderColumn.EntryDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.EntryDate}`,
            }),
            dataIndex: 'entryDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 100,
          },
          {
            key: WorkorderColumn.EstimatedLeaveDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.EstimatedLeaveDate}`,
            }),
            dataIndex: 'estimatedLeaveDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 100,
          },
          {
            key: WorkorderColumn.AuthorizationDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.AuthorizationDate}`,
            }),
            dataIndex: 'authorizationDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 100,
          },
          {
            key: WorkorderColumn.CloseDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.CloseDate}`,
            }),
            dataIndex: 'closingDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 100,
          },
          {
            key: WorkorderColumn.ReturnDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.ReturnDate}`,
            }),
            dataIndex: 'returnDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 100,
          },
          {
            key: WorkorderColumn.Status,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Status}`,
            }),
            dataIndex: 'status',
            render: (value: WorkOrderComplementStatus) => (
              <StatusTag
                color={workorderComplementColor[value]}
                label={intl.formatMessage({ id: `workOrders.${value}` })}
              />
            ),
            ellipsis: true,
            width: 90,
          },
          {
            key: WorkorderColumn.Type,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.Type}`,
            }),
            dataIndex: 'type',
            render: (value) => (
              <Tag color="processing">
                {intl.formatMessage({ id: `workOrders.${value}` })}
              </Tag>
            ),
            ellipsis: true,
            width: 150,
          },
          {
            key: 'actions',
            title: intl.formatMessage({
              id: 'commons.list.header.action',
            }),
            render: (_, item) => (
              <Group gap={10} position="right" align="center">
                <MigrationWorkOrderBinded
                  complementId={item.boundWorkOrder?.complementId}
                  workOrderId={item.boundWorkOrder?.workOrderId}
                />

                <div
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <EyeIcon
                    disabled={
                      !hasPermissions([
                        Authorities.MOVIMENTATION_WORKORDER_VIEW,
                      ])
                    }
                    iconProps={{
                      onClick: () => {
                        setWorkOrder(item);
                        action.open();
                      },
                    }}
                  />
                </div>

                <MigrationBindComplements migrationId={item.id} />
              </Group>
            ),
            ellipsis: true,
            width: 100,
            fixed: 'right',
            align: 'right',
          },
        ]}
      />

      <MigrationConsultDetailModal
        opened={opened}
        onClose={action.close}
        workOrder={workOrder}
      />
    </>
  );
}
